import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Check, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { services } from '../mock/data';

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = services.find(s => s.slug === slug);

  if (!service) {
    return (
      <div className="min-h-screen bg-black text-white pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Service Not Found</h1>
          <Link to="/services">
            <Button className="bg-white text-black hover:bg-white/90">
              Back to Services
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pt-20">
      {/* Hero */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <Link to="/services" className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Services
          </Link>
          <h1 className="text-5xl md:text-7xl font-bold mb-6">{service.title}</h1>
          <p className="text-xl md:text-2xl text-white/80 max-w-3xl mx-auto">
            {service.description}
          </p>
        </div>
      </section>

      {/* Service Details */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-3xl font-bold mb-4">About This Service</h2>
                <p className="text-white/70 text-lg leading-relaxed">
                  {service.fullDescription}
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold mb-6">Key Features</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {service.features.map((feature, index) => (
                    <Card key={index} className="bg-white/5 border-white/10 p-4 flex items-start space-x-3">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white/10 flex items-center justify-center mt-0.5">
                        <Check className="h-4 w-4" />
                      </div>
                      <span className="text-white/80">{feature}</span>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Related Image */}
              <div className="rounded-lg overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-80 object-cover"
                />
              </div>
            </div>

            {/* Sidebar - Booking Card */}
            <div className="lg:col-span-1">
              <Card className="bg-white text-black p-8 sticky top-24">
                <h3 className="text-2xl font-bold mb-4">Book This Service</h3>
                <p className="text-black/70 mb-6">
                  Get a personalised quote for {service.title.toLowerCase()} service
                </p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center space-x-3 text-sm">
                    <Check className="h-5 w-5 flex-shrink-0" />
                    <span>Instant quote response</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm">
                    <Check className="h-5 w-5 flex-shrink-0" />
                    <span>Professional chauffeurs</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm">
                    <Check className="h-5 w-5 flex-shrink-0" />
                    <span>Luxury vehicles</span>
                  </div>
                  <div className="flex items-center space-x-3 text-sm">
                    <Check className="h-5 w-5 flex-shrink-0" />
                    <span>24/7 availability</span>
                  </div>
                </div>

                <Link to="/booking" className="block mb-4">
                  <Button className="w-full bg-black text-white hover:bg-black/90">
                    Request a Quote
                  </Button>
                </Link>

                <div className="text-center">
                  <div className="text-sm text-black/60 mb-2">Or call us directly</div>
                  <a 
                    href="tel:03333357733"
                    className="flex items-center justify-center space-x-2 text-black font-semibold hover:text-black/80 transition-colors"
                  >
                    <Phone className="h-4 w-4" />
                    <span>0333 335 7733</span>
                  </a>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Other Services */}
      <section className="py-24 bg-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">Other Services</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {services
              .filter(s => s.id !== service.id)
              .slice(0, 3)
              .map((otherService) => (
                <Link key={otherService.id} to={`/services/${otherService.slug}`}>
                  <Card className="group bg-white/5 border-white/10 hover:bg-white/10 transition-all duration-500 overflow-hidden h-full">
                    <div className="aspect-[4/3] overflow-hidden">
                      <img
                        src={otherService.image}
                        alt={otherService.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold mb-2">{otherService.title}</h3>
                      <p className="text-white/60 text-sm line-clamp-2">{otherService.description}</p>
                    </div>
                  </Card>
                </Link>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
